import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Check } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", clinic_name: "", city: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { ...form });
      setDone(true);
      toast.success("Thank you. We'll be in touch within 24h.");
      setForm({ name: "", email: "", phone: "", clinic_name: "", city: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="overline">Contact</div>
        <h1 className="font-display text-5xl tracking-tight mt-3">Talk to a specialist.</h1>
        <p className="text-[var(--fm-text-2)] mt-4 leading-relaxed max-w-md">Whether you're outfitting a new operatory or replacing a single piece, our team will respond within 24 business hours.</p>

        <div className="mt-10 space-y-5">
          <div className="flex gap-4"><Mail className="text-[var(--fm-primary)] mt-1" size={18} strokeWidth={1.5} /><div><div className="overline">Email</div><div className="text-sm mt-1">sachman21@gmail.com</div></div></div>
          <div className="flex gap-4"><Phone className="text-[var(--fm-primary)] mt-1" size={18} strokeWidth={1.5} /><div><div className="overline">Phone / WhatsApp</div><div className="text-sm mt-1">+91 82888 52339</div></div></div>
          <div className="flex gap-4"><MapPin className="text-[var(--fm-primary)] mt-1" size={18} strokeWidth={1.5} /><div><div className="overline">Office</div><div className="text-sm mt-1">69 MCC Avenue, Aerocity,<br />Mohali, Punjab, India</div></div></div>
        </div>
      </div>

      <div className="lg:col-span-7">
        <form onSubmit={submit} className="bg-white border border-[var(--fm-border)] p-8 space-y-4">
          {done && (
            <div className="bg-[var(--fm-sage)] p-4 flex items-center gap-3 text-sm"><Check size={16} className="text-[var(--fm-primary)]" /> Your enquiry has been received.</div>
          )}
          <div className="overline text-[var(--fm-accent)]">Send us a message</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" required />
            <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" required />
            <Field label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" required />
            <Field label="Clinic name" value={form.clinic_name} onChange={(v) => setForm({ ...form, clinic_name: v })} testid="contact-clinic" />
            <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="contact-city" className="sm:col-span-2" />
          </div>
          <div>
            <label className="overline">Message *</label>
            <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} data-testid="contact-message" className="w-full border border-[var(--fm-border)] mt-2 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[var(--fm-primary)]" placeholder="Tell us about your practice and what you're looking for…" />
          </div>
          <button disabled={busy} data-testid="contact-submit" className="fm-btn-primary w-full">{busy ? "Sending…" : "Send enquiry"}</button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required, className = "" }) {
  return (
    <div className={className}>
      <label className="overline">{label}</label>
      <input required={required} type={type} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} className="w-full border border-[var(--fm-border)] mt-2 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[var(--fm-primary)]" />
    </div>
  );
}
